import { Injectable } from '@angular/core';

export interface RegistrationDraft {
  name?: string;
  email?: string;
  branchCode?: string;
  installationId?: string;
  installationName?: string;
  separationMonth?: string;
}

const STORAGE_KEY = 'cpd.registration.draft';

/**
 * Registration in progress, held across a reload.
 *
 * 01-account acceptance criterion 6: refreshing mid-registration does not lose
 * step 1. Registration is a dialog, so a reload closes it; without somewhere to
 * put the answers, a member who refreshes on step 2 is sent back to type their
 * name again.
 *
 * sessionStorage, not localStorage: this is a half-finished form, and it should
 * not still be sitting there tomorrow on a shared machine. It is cleared the
 * moment the account is made.
 *
 * Every access is guarded. Private browsing and storage-blocking settings throw
 * on read as well as on write, and a member who cannot persist a draft should
 * still be able to register: they lose the resume, not the flow.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationDraftService {
  public read(): RegistrationDraft {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as RegistrationDraft) : {};
    } catch {
      return {};
    }
  }

  /** Merges into whatever is already held, so each step writes only its own fields. */
  public patch(changes: RegistrationDraft): void {
    try {
      const next = { ...this.read(), ...changes };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // No draft survives the reload. The flow still works.
    }
  }

  public clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear if nothing could be written.
    }
  }
}
